import React, { useEffect, useState } from 'react';
import { Tabs } from 'antd';
import {
  Input,
  Button,
  Form,
  Typography,
  Divider,
  Tooltip,
  Switch,
  Row,
  Col,
  Select,
} from 'antd';
import axios from '../shared/axios';
import { BarChart, CartesianGrid, XAxis, YAxis, Bar } from 'recharts';
import { useParams } from 'react-router-dom';
import { useForm } from 'antd/lib/form/Form';
import { formatFeedbackCSVData } from '../../utilities/formatData';
import { CSVLink } from 'react-csv';
import TableWithSorter from '../Common/TableWithSorter';
import './FeedbackPageBySurveyId.scss';

const { Title } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const OVERALL_RATING_TYPE = 'overallRating';
const className = 'c-FeedbackPageSurveyById';

const FeedbackPageBySurveyId = () => {
  const [showFeedback, setShowFeedback] = useState(false);
  const [ratingsChartData, setRatingsChartData] = useState([]);
  const [tableData, setTableData] = useState({});
  const [ratingTableData, setRatingTableData] = useState({});
  const [visibleColumns, setVisibleColumns] = useState(false);
  const [fullAnswerData, setFullAnswerData] = useState([]);
  const [feedbackCSVData, setFeedbackCSVData] = useState({});
  const [filterForm] = useForm();
  const [questions, setQuestions] = useState([]);

  const { id } = useParams();

  useEffect(() => {
    if (id) {
      getFeedback(id);
    }
  }, [id]);

  let formatData = (data, survey) => {
    let adminColumns = () => [
      {
        title: 'User Id',
        dataIndex: 'userId',
        key: 'userId',
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
      },
      {
        title: 'Answer',
        dataIndex: 'answer',
        key: 'answer',
      },
      {
        title: 'Overall CreatorRating',
        dataIndex: 'rating',
        key: 'rating',
      },
    ];

    let trainerColumns = () => [
      {
        title: 'Answer',
        dataIndex: 'answer',
        key: 'answer',
      },
      {
        title: 'Overall CreatorRating',
        dataIndex: 'rating',
        key: 'rating',
      },
    ];

    const ratingQuestionId = Object.keys(data[0].questions).find(
      (questionId) =>
        data[0].questions[questionId].tag &&
        data[0].questions[questionId].tag === OVERALL_RATING_TYPE
    );

    let ratingsInfo = {};

    let tableDataFromAPI = {};
    let ratingTableDataFromApi = {};
    data.map((user) => {
      //Iterate over the question data
      for (const [key, value] of Object.entries(user.questions)) {
        if (value.type === 'rating') {
          const rating = value.answer * 10;

          if (!ratingsInfo[key]) {
            let ratingsChartStats = new Array(10);
            for (let i = 0; i < 10; i++) {
              ratingsChartStats[i] = {
                name: `${i + 1}⭐`,
                numberOfReviews: 0,
              };
            }

            ratingsInfo[key] = {
              questionLabel: value.question,
              feedbackCount: 0,
              totalSumOfFeedback: 0,
              feedbackAvg: 0,
              chartStats: ratingsChartStats,
            };
          }

          ratingsInfo[key].chartStats[rating - 1].numberOfReviews++;
          ratingsInfo[key].feedbackCount = ratingsInfo[key].feedbackCount + 1;
          ratingsInfo[key].totalSumOfFeedback =
            ratingsInfo[key].totalSumOfFeedback + rating;
          ratingsInfo[key].feedbackAvg = (
            ratingsInfo[key].totalSumOfFeedback / ratingsInfo[key].feedbackCount
          ).toFixed(2);
        }
        let formattedTableData = {
          label: value.question,
          adminColumns: adminColumns(),
          trainerColumns: trainerColumns(),
          data: [],
        };
        if (value.type !== 'rating') {
          if (!tableDataFromAPI[key]) {
            tableDataFromAPI[key] = formattedTableData;
          }
          const userAnswer = {
            key: value._id,
            userId: user.userData?.learnerId
              ? user.userData?.learnerId
              : user.userData?.trainerId,
            name: user.userData?.firstName + ' ' + user.userData?.lastName,
            email: user.userData?.email,
            answer:
              value.type === 'multiAnswerDropdown'
                ? value.answer?.join(', ')
                : value.answer,
            rating: user.questions[ratingQuestionId]
              ? user.questions[ratingQuestionId]?.answer * 10
              : '',
          };
          tableDataFromAPI[key].data.push(userAnswer);
        } else {
          if (!ratingTableDataFromApi[key]) {
            ratingTableDataFromApi[key] = formattedTableData;
          }
          const userAnswer = {
            key: value._id,
            userId: user.userData?.learnerId
              ? user.userData?.learnerId
              : user.userData?.trainerId,
            name: user.userData?.firstName + ' ' + user.userData?.lastName,
            email: user.userData?.email,
            answer: value?.answer * 10,
            rating: user.questions[ratingQuestionId]
              ? user.questions[ratingQuestionId]?.answer * 10
              : '',
          };
          ratingTableDataFromApi[key].data.push(userAnswer);
        }
      }
    });

    setRatingsChartData(Object.values(ratingsInfo));

    setTableData({
      isAvailable: true,
      data: tableDataFromAPI,
    });

    setRatingTableData({
      isAvailable: true,
      data: ratingTableDataFromApi,
    });

    const averageRating = ratingsInfo[ratingQuestionId]?.feedbackAvg;

    setFeedbackCSVData(
      formatFeedbackCSVData(
        { ...tableDataFromAPI, ...ratingTableDataFromApi },
        averageRating,
        survey.courseOfferInfo?.code,
        survey.unitOfferInfo?.unitDescription
      )
    );
  };

  let createTableJSX = (data) => {
    let tableJSX = [];
    for (const [questionId, questionInfo] of Object.entries(data)) {
      tableJSX.push(
        <section>
          <Divider>
            <Title level={4}>{questionInfo.label}</Title>
          </Divider>
          <TableWithSorter
            dataSource={questionInfo.data}
            columns={
              visibleColumns
                ? questionInfo.trainerColumns
                : questionInfo.adminColumns
            }
            pagination={{
              pageSize: 30,
            }}
            className={`${className}__table`}
          />
        </section>
      );
    }
    return tableJSX;
  };

  let getFeedback = (id) => {
    let FeedbackURL = `${process.env.REACT_APP_BACKEND_URL}/answers/feedback-form/${id}`;
    let params;
    if (!id) {
      alert('Enter a valid surveyid');
      return;
    }
    axios
      .get(FeedbackURL, {
        params,
      })
      .then((response) => {
        const surveyQuestions = response.data.answerInfo[0]?.questions
          ? Object.keys(response.data.answerInfo[0]?.questions).map((qnId) => {
              return {
                questionId: qnId,
                question: response.data.answerInfo[0]?.questions[qnId].question,
                type: response.data.answerInfo[0]?.questions[qnId].type,
              };
            })
          : [];
        setFullAnswerData(response.data.answerInfo);
        setQuestions(surveyQuestions);
        formatData(response.data.answerInfo, response.data);
        setShowFeedback(true);
      })
      .catch((error) => {
        console.log('Error getting data from the admin backend:', error);
        alert('Getting data from the backend failed!');
        setShowFeedback(false);
      });
  };

  const onSearch = (data) => {
    const qnId = data.param;
    let answer = data.val;
    const filteredAnswer = fullAnswerData.filter((item) => {
      if (
        item.questions[qnId].type === 'rating' ||
        item.questions[qnId].type === 'number'
      ) {
        answer = parseFloat(answer);
      }
      return item.questions[qnId].answer === answer;
    });
    if (filteredAnswer.length === 0) {
      alert('Answer does not exist with this question');
      return;
    }
    formatData(filteredAnswer);
  };
  const onReset = () => {
    filterForm.resetFields();
    formatData(fullAnswerData);
  };
  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
        className={className}
      >
        <header style={{ width: '100%' }}>
          <Title style={{ float: 'left' }} level={2}>
            Answer
          </Title>
          {feedbackCSVData.data && feedbackCSVData.data.length > 0 && (
            <div
              style={{
                marginLeft: '10px',
                float: 'right',
                position: 'relative',
              }}
            >
              <Button type="primary">
                <CSVLink
                  data={feedbackCSVData.data}
                  headers={feedbackCSVData.headers}
                  filename={feedbackCSVData.filename}
                >
                  Export to CSV
                </CSVLink>
              </Button>
            </div>
          )}
        </header>

        <Divider />
        {showFeedback && (
          <>
            <section className="grey-section">
              <Title level={4}>Filter By Question</Title>
              <Form form={filterForm} onFinish={onSearch}>
                <Row gutter={16}>
                  <Col span={16}>
                    <Form.Item name="param" rules={[{ required: true }]}>
                      <Select placeholder="Search by...">
                        {questions.map((question) => {
                          return (
                            <Option value={question.questionId}>
                              {question.question}
                            </Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row span={16}>
                  <Col span={16}>
                    <Form.Item name="val" rules={[{ required: true }]}>
                      <Input placeholder="Enter the answer you want to search by..." />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={16}>
                    <Form.Item>
                      <Button type="primary" htmlType="submit">
                        Search
                      </Button>
                      <Button htmlType="button" onClick={onReset}>
                        Reset
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </section>

            <Tabs defaultActiveKey="1" type="card" size="middle">
              <TabPane tab="Rating graph" key="1">
                <section>
                  {ratingsChartData.map(
                    ({ questionLabel, feedbackAvg, chartStats }, key) => {
                      const label = `${questionLabel}: ${feedbackAvg}⭐`;

                      return (
                        <section
                          key={key}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}
                        >
                          <Divider>
                            <Title level={4}>{label}</Title>
                          </Divider>
                          <BarChart width={730} height={500} data={chartStats}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="numberOfReviews" fill="#8884d8" />
                          </BarChart>
                        </section>
                      );
                    }
                  )}
                </section>
              </TabPane>
              <TabPane tab="Rating table" key="2">
                {ratingTableData.isAvailable &&
                  createTableJSX(ratingTableData.data)}
              </TabPane>
            </Tabs>

            <Switch
              checkedChildren="Admin View"
              unCheckedChildren="Trainer View"
              defaultChecked={true}
              onChange={() => setVisibleColumns((prev) => !prev)}
            />

            {tableData.isAvailable && createTableJSX(tableData.data)}
          </>
        )}
      </div>
    </>
  );
};

export default FeedbackPageBySurveyId;
